import {useIntegrator} from "../../integrator.context";
import {useApp} from "../../app.context";
import LocalizationInput from "../../elements/localizationInput.element";
import {AddTechNodeCommand} from "../../commands/addTechNode.command";
import {RemoveTechNodeCommand} from "../../commands/removeTechNode.command";
import TechUnlock from "./techUnlock";
import {ROOT_ID} from "../../utils/techTree.utils";

// Editing surface for the selected tech node. The canvas draws circles that
// shrink with zoom, so the controls that used to live inside a node box can't
// live there any more — they move here, where they're always at a usable size
// and where react-dnd still works on real DOM elements (HTML5 drag sources on
// SVG elements are not portable).
//
// Always on screen, and never closable: with root absent from the canvas this
// pane is the only way to add a tier-1 node, so it falls back to root whenever
// nothing is selected rather than disappearing. Clicking empty canvas is what
// returns here.
function TechNodeInspector(props: {id?: string; onSelect(id: string | undefined): void}) {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue, confirmDelete} = useApp();

    // Falls back to root both for "nothing selected" and for a selection that
    // no longer resolves — undo/redo can strand an id the canvas hasn't cleared.
    const node = (props.id !== undefined ? state.techTree?.find(t => t.id === props.id) : undefined)
        ?? state.techTree?.find(t => t.id === ROOT_ID);
    // Only reachable with a techTree that has no root at all, which no branch has.
    if (!node) return null;

    const isRoot = node.id === ROOT_ID;
    const unlocks = node.unlocks ?? [];
    const placed = Number.isInteger(node.x) && Number.isInteger(node.y);

    return (
        <div className={'layout vertical gap box'} style={{gap: 8}}>
            <p className={'text header'}>{isRoot ? 'Root Details' : 'Node Details'}</p>

            <div className={'layout horizontal center gap'}>
                {/* Root carries no localized name and can't be deleted. */}
                {!isRoot && <LocalizationInput id={node.id}/>}
                {isRoot &&
                    <p style={{flex: 1, color: 'var(--muted-text-color)'}}
                       title={'Every node hangs off root. It has no position of its own and is not drawn on the canvas.'}>
                        Tree root — not drawn
                    </p>}
                <button onClick={() => executeCommand(new AddTechNodeCommand(node.id))}
                        title={isRoot ? 'Add a tier-1 node' : 'Add a child node'}>
                    <span className={'fa-solid fa-add'}/>
                </button>
                {!isRoot &&
                    <button title={'Delete this node, and any descendant it is the only route to'}
                            onClick={async () => {
                                if (await confirmDelete(getLocalizationValue(node.id), 'tech node')) {
                                    executeCommand(new RemoveTechNodeCommand(node.id));
                                    props.onSelect(undefined);
                                }
                            }}>
                        <span className={'fa-solid fa-trash'}/>
                    </button>}
            </div>

            {/* Root has no cell on the canvas, so there's nothing to report. */}
            {!isRoot &&
                <div className={'layout horizontal center stretch'}>
                    <span style={{color: 'var(--muted-text-color)', fontSize: 12}}>Position</span>
                    <span style={{fontSize: 12, color: placed ? 'white' : 'var(--muted-text-color)'}}
                          title={placed
                              ? 'Saved in the ruleset'
                              : 'Computed — drag the node, or use Place in the canvas toolbar, to save it'}>
                        {placed ? `${node.x}, ${node.y}` : 'computed'}
                    </span>
                </div>}

            <div className={'layout vertical gap'} style={{gap: 4}}>
                <span style={{color: 'var(--muted-text-color)', fontSize: 12}}>
                    Unlocks {unlocks.length > 0 ? `(${unlocks.length})` : ''}
                </span>
                <div className={'layout horizontal gap'} style={{flexWrap: 'wrap'}}>
                    {unlocks.length === 0 &&
                        <span style={{color: 'var(--muted-text-color)', fontSize: 12}}>
                            Drag an entity from below onto a node.
                        </span>}
                    {/* cancelDrag is a no-op here: it exists to stop the old
                        canvas pan getting stuck when an HTML5 drag began, and
                        the inspector has no pan to cancel. */}
                    {unlocks.map(id => <TechUnlock key={id} nodeId={node.id} id={id} cancelDrag={() => undefined}/>)}
                </div>
            </div>
        </div>
    );
}

export default TechNodeInspector;
